
import React, { useState, useRef, useEffect } from 'react';
import { QUESTIONS, RESULTS } from './constants';
import { ChevronLeft, Share2, ExternalLink, Download, Home, RotateCcw } from 'lucide-react';
import html2canvas from 'html2canvas';
import { QRCodeSVG } from 'qrcode.react';
import confetti from 'canvas-confetti';
import './App.css';

type Screen = 'start' | 'quiz' | 'result';

const App: React.FC = () => {
  const [screen, setScreen] = useState<Screen>('start');
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [result, setResult] = useState<any>(null);
  const posterRef = useRef<HTMLDivElement>(null);

  const startQuiz = () => {
    setScreen('quiz');
    setCurrentIdx(0);
    setAnswers([]);
  };

  const handleAnswer = (value: number) => {
    const newAnswers = [...answers];
    newAnswers[currentIdx] = value;
    setAnswers(newAnswers);

    if (currentIdx < QUESTIONS.length - 1) {
      setCurrentIdx(currentIdx + 1);
    } else {
      calculateResult(newAnswers);
    }
  };

  const goBack = () => {
    if (currentIdx > 0) {
      setCurrentIdx(currentIdx - 1);
    }
  };

  const calculateResult = (finalAnswers: number[]) => {
    const scores = {
      social: 0,
      mental: 0,
      emotional: 0,
      life: 0
    };

    QUESTIONS.forEach((q, idx) => {
      (scores as any)[q.dimension] += finalAnswers[idx];
    });

    const code = 
      (scores.social >= 14 ? 'E' : 'I') +
      (scores.mental >= 14 ? 'C' : 'B') +
      (scores.emotional >= 14 ? 'F' : 'T') +
      (scores.life >= 14 ? 'J' : 'P');

    // 10% 概率触发“中国男足”彩蛋
    if (Math.random() < 0.1) {
      setResult((RESULTS as any)['SPECIAL_FOOTBALL']);
    } else {
      setResult((RESULTS as any)[code]);
    }
    
    setScreen('result');
    confetti({
      particleCount: 150,
      spread: 70,
      origin: { y: 0.6 }
    });
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('链接已复制到剪贴板！');
  };

  const savePoster = async () => {
    if (posterRef.current) {
      const canvas = await html2canvas(posterRef.current, {
        useCORS: true,
        scale: 2,
        backgroundColor: '#f8fafc'
      });
      const link = document.createElement('a');
      link.download = `NBTI测试结果-${result.title}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    }
  };

  const restart = () => {
    setScreen('start');
    setResult(null);
    setAnswers([]);
    setCurrentIdx(0);
  };

  return (
    <div className="app-container">
      {screen === 'start' && (
        <div className="start-screen fade-in">
          <div className="header-logo">
            <img 
              src="/anxinshe-logo.png" 
              alt="安心舍" 
              className="logo-img clickable" 
              onClick={() => window.location.href = 'https://app.anxinshe.com'}
            />
            <h1 className="brand-name">安心舍</h1>
          </div>
          <div className="main-card">
            <div className="title-section">
              <h2 className="app-title">NBTI 性格测试</h2>
              <p className="app-subtitle">解锁你的 16 款搞怪人设</p>
            </div>
            <div className="hero-emoji">🤪</div>
            <button className="btn btn-primary" onClick={startQuiz}>开始测试</button>
            <div className="action-row">
              <button className="btn btn-outline" onClick={copyLink}>
                <Share2 size={18} /> 分享链接
              </button>
              <button className="btn btn-outline" onClick={() => window.location.href = 'https://app.anxinshe.com'}>
                <ExternalLink size={18} /> 访问安心舍
              </button>
            </div>
          </div>
          <p className="disclaimer">本测试纯属娱乐，主打搞怪解压 ✨</p>
        </div>
      )}

      {screen === 'quiz' && (
        <div className="quiz-screen fade-in">
          <div className="progress-bar">
            <div 
              className="progress-inner" 
              style={{ width: `${((currentIdx + 1) / QUESTIONS.length) * 100}%` }}
            ></div>
          </div>
          <div className="quiz-header">
            <span className="q-count">QUESTION {currentIdx + 1} / {QUESTIONS.length}</span>
          </div>
          <h2 className="question-text">{QUESTIONS[currentIdx].text}</h2>
          <div className="options-list">
            {QUESTIONS[currentIdx].options.map((opt, i) => (
              <button 
                key={i} 
                className="option-btn"
                onClick={() => handleAnswer(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {currentIdx > 0 && (
            <button className="back-btn" onClick={goBack}>
              <ChevronLeft size={20} /> 返回上一题
            </button>
          )}
        </div>
      )}

      {screen === 'result' && result && (
        <div className="result-screen fade-in">
          <div className="poster-container" ref={posterRef}>
            <div className="poster-inner">
              <div className="poster-header">
                <img src="/anxinshe-logo.png" alt="logo" className="poster-logo" />
                <span className="poster-brand">安心舍 · NBTI 性格测试</span>
              </div>
              <div className="poster-main">
                <div className="res-label">你的 NBTI 结果</div>
                <h1 className="res-title">{result.title}</h1>
                <div className="tags">
                  {result.tags.map((tag: string, i: number) => (
                    <span key={i} className="tag">#{tag}</span>
                  ))}
                </div>
                <p className="res-desc">{result.desc}</p>
              </div>
              <div className="poster-footer">
                <div className="qr-box">
                  <QRCodeSVG value={window.location.href} size={80} level="H" />
                  <p className="qr-hint">长按扫码测试你的搞怪人设</p>
                </div>
              </div>
            </div>
          </div>

          <div className="result-actions">
            <button className="btn btn-primary" onClick={savePoster}>
              <Download size={18} /> 保存海报
            </button>
            <div className="action-row">
              <button className="btn btn-outline" onClick={copyLink}>
                <Share2 size={18} /> 分享好友
              </button>
              <button className="btn btn-outline" onClick={restart}>
                <RotateCcw size={18} /> 重新测试
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
